import { Controller } from "@hotwired/stimulus";
import { APPLY_STORAGE_KEY } from "./simulation_controller";

export default class extends Controller {
  static targets = [
    // Input fields
    "grossPrice",
    "vatRate",
    "wastagePercentage",
    "markupCoefficient",
    // Display targets
    "totalIngredientCost",
    "netPrice",
    "unitCostWithWastage",
    "grossMargin",
    "marginRate",
    // Hidden form fields
    "netPriceField",
    "totalIngredientCostField",
    "unitCostField",
    "unitCostWithWastageField",
    "grossMarginField",
    // Sub-recipe targets
    "subRecipeRow",
    "subRecipeUnitPrice",
    "subRecipeTotalCost",
    // Profitability visual targets
    "profitabilityRow",
    "profitabilityBanner",
    "profitabilityIndicator",
    "profitabilityTitle",
    "profitabilityDescription",
    "profitabilityBadge",
    "grossMarginStat",
    "marginRateStat",
    "marginRateIndicator",
    "recommendationsPanel",
    "recommendationText",
  ];

  connect() {
    this.applyPendingSimulation();
    this.recalculate();
  }

  applyPendingSimulation() {
    const raw = sessionStorage.getItem(APPLY_STORAGE_KEY);
    if (!raw) return;

    sessionStorage.removeItem(APPLY_STORAGE_KEY);

    try {
      const values = JSON.parse(raw);
      if (this.hasGrossPriceTarget && values.gross_price != null) {
        this.grossPriceTarget.value = values.gross_price;
      }
      if (this.hasVatRateTarget && values.vat_rate != null) {
        this.vatRateTarget.value = values.vat_rate;
      }
      if (this.hasWastagePercentageTarget && values.wastage_percentage != null) {
        this.wastagePercentageTarget.value = values.wastage_percentage;
      }
      if (this.hasMarkupCoefficientTarget && values.markup_coefficient != null) {
        this.markupCoefficientTarget.value = values.markup_coefficient;
      }
      this.showApplyNotice();
    } catch (_error) {
      // ignore malformed payload
    }
  }

  showApplyNotice() {
    const notice = document.createElement("div");
    notice.className = "alert alert-success mb-4";
    notice.setAttribute("role", "status");
    notice.innerHTML =
      "<span>Valeurs appliquées depuis le simulateur — n'oubliez pas d'enregistrer.</span>";
    this.element.prepend(notice);
    setTimeout(() => notice.remove(), 8000);
  }

  // Primary entry point for all calculations
  recalculate(event) {
    // Check if the price input triggered the change
    if (event && event.target === this.grossPriceTarget) {
      this.updateCoefficientFromPrice();
    } else {
      this.updatePriceFromCoefficient();
    }

    // Perform final calculations and update the entire display
    const values = this.getValues();
    const calculated = this.performCalculations(values);
    this.updateDisplay(calculated);
    this.updateHiddenFields(calculated);
  }

  updatePriceFromCoefficient() {
    const totalIngredientCost = this.getTotalIngredientCost();
    const portionsCount = this.getPortionsCount();
    const wastagePercentage = this.parseValue(
      this.wastagePercentageTarget.value
    );
    const markupCoefficient = this.parseValue(
      this.markupCoefficientTarget.value
    );
    const vatRate = this.parseValue(this.vatRateTarget.value);

    const unitCost =
      portionsCount > 0 ? totalIngredientCost / portionsCount : 0;
    const unitCostWithWastage = unitCost * (1 + wastagePercentage / 100);

    if (unitCostWithWastage > 0 && markupCoefficient > 0) {
      const netPrice = unitCostWithWastage * markupCoefficient;
      const grossPrice = netPrice * (1 + vatRate / 100);
      this.grossPriceTarget.value = grossPrice.toFixed(2);
    }
  }

  updateCoefficientFromPrice() {
    const totalIngredientCost = this.getTotalIngredientCost();
    const portionsCount = this.getPortionsCount();
    const wastagePercentage = this.parseValue(
      this.wastagePercentageTarget.value
    );
    const grossPrice = this.parseValue(this.grossPriceTarget.value);
    const vatRate = this.parseValue(this.vatRateTarget.value);

    const unitCost =
      portionsCount > 0 ? totalIngredientCost / portionsCount : 0;
    const unitCostWithWastage = unitCost * (1 + wastagePercentage / 100);

    if (unitCostWithWastage > 0 && grossPrice > 0) {
      const netPrice = grossPrice / (1 + vatRate / 100);
      const markupCoefficient = netPrice / unitCostWithWastage;
      this.markupCoefficientTarget.value = markupCoefficient.toFixed(2);
    }
  }

  getValues() {
    return {
      grossPrice: this.parseValue(this.grossPriceTarget.value),
      vatRate: this.parseValue(this.vatRateTarget.value) || 20,
      wastagePercentage: this.parseValue(this.wastagePercentageTarget.value),
      markupCoefficient:
        this.parseValue(this.markupCoefficientTarget.value) || 4.0,
      totalIngredientCost: this.getTotalIngredientCost(),
    };
  }

  getTotalIngredientCost() {
    let total = 0;

    // Sum costs from existing ingredient rows (old table system)
    this.element
      .querySelectorAll(
        '[data-ingredients-table-target="tableBody"] tr:not(.hidden)'
      )
      .forEach((row) => {
        const costElement = row.querySelector(
          '[data-ingredients-table-target="rowCost"]'
        );
        if (costElement) {
          const costText = costElement.textContent;
          total += this.parseValue(costText.replace(/[€\s]/g, ""));
        }
      });

    // Sum costs from new spreadsheet-style editable rows (both existing and new)
    this.element
      .querySelectorAll(
        '[data-ingredients-table-spreadsheet-target="editableRow"]'
      )
      .forEach((row) => {
        // Skip hidden rows (visually removed ingredients)
        if (row.style.display === "none") {
          return;
        }

        // Skip rows marked for destruction
        if (row.dataset.destroyed === "true") {
          return;
        }

        const costElement = row.querySelector(
          '[data-ingredients-table-spreadsheet-target="rowCostDisplay"]'
        );
        if (
          costElement &&
          costElement.textContent !== "-" &&
          costElement.textContent.trim() !== ""
        ) {
          const costText = costElement.textContent;
          const cost = this.parseValue(costText.replace(/[€\s]/g, ""));
          if (cost > 0) {
            total += cost;
          }
        }
      });

    // Add cost from the "pending" ingredient in the add row (old system)
    const pendingCostEl = this.element.querySelector(
      '[data-ingredients-table-target="costDisplay"]'
    );
    if (pendingCostEl) {
      const pendingCostText = pendingCostEl.textContent;
      total += this.parseValue(pendingCostText.replace(/[€\s]/g, ""));
    }

    // ADD SUB-RECIPE COSTS! 🎯
    const subRecipeCost = this.getSubRecipesTotalCost();
    total += subRecipeCost;

    // Debug logging (remove in production)
    console.log("Cost calculation breakdown:", {
      ingredientCost: total - subRecipeCost,
      subRecipeCost: subRecipeCost,
      totalCost: total,
    });

    return total;
  }

  // NEW METHOD: Calculate sub-recipes cost from DOM (table format)
  getSubRecipesTotalCost() {
    let subRecipesTotal = 0;

    // Find all sub-recipe rows in the table
    const subRecipeRows = this.element.querySelectorAll(
      '[data-cost-calculator-target="subRecipeRow"]'
    );

    subRecipeRows.forEach((row) => {
      // Skip if row is hidden or marked for destruction
      if (row.style.display === "none") {
        return;
      }

      const destroyInput = row.querySelector('input[name*="[_destroy]"]');
      if (destroyInput?.value === "1") {
        return;
      }

      const recipeSelect = row.querySelector('select[name*="[recipe_id]"]');
      const quantityInput = row.querySelector('input[name*="[quantity]"]');

      if (!recipeSelect?.value || !quantityInput?.value) {
        return;
      }

      const recipeId = parseInt(recipeSelect.value);
      const quantity = this.parseValue(quantityInput.value);

      if (recipeId && quantity > 0) {
        // Get the cost from the recipe option's data attribute
        const recipeOption = recipeSelect.querySelector(
          `option[value="${recipeId}"]`
        );
        if (recipeOption && recipeOption.dataset.unitCost) {
          const unitCost = this.parseValue(recipeOption.dataset.unitCost);
          subRecipesTotal += quantity * unitCost;
        }
      }
    });

    return subRecipesTotal;
  }

  performCalculations(values) {
    const { grossPrice, vatRate, wastagePercentage, totalIngredientCost } =
      values;

    const netPrice = grossPrice / (1 + vatRate / 100);
    const portionsCount = this.getPortionsCount();
    const unitCost =
      portionsCount > 0 ? totalIngredientCost / portionsCount : 0;
    const unitCostWithWastage = unitCost * (1 + wastagePercentage / 100);
    const grossMargin = netPrice - unitCostWithWastage;
    const marginRate = netPrice > 0 ? (grossMargin / netPrice) * 100 : 0;

    return {
      netPrice,
      totalIngredientCost,
      unitCost,
      unitCostWithWastage,
      grossMargin,
      marginRate,
    };
  }

  getPortionsCount() {
    const portionsInput = document.querySelector('[name*="portions_count"]');
    return this.parseValue(portionsInput?.value) || 1;
  }

  updateDisplay(calculated) {
    this.totalIngredientCostTarget.textContent = this.formatCurrency(
      calculated.totalIngredientCost
    );
    this.netPriceTarget.textContent = this.formatCurrency(calculated.netPrice);
    this.unitCostWithWastageTarget.textContent = this.formatCurrency(
      calculated.unitCostWithWastage
    );
    this.grossMarginTarget.textContent = this.formatCurrency(
      calculated.grossMargin
    );
    this.marginRateTarget.textContent = `${calculated.marginRate.toFixed(1)}%`;

    // Update profitability visual indicators
    this.updateProfitabilityDisplay(calculated);
  }

  updateHiddenFields(calculated) {
    this.netPriceFieldTarget.value = calculated.netPrice.toFixed(2);
    this.totalIngredientCostFieldTarget.value =
      calculated.totalIngredientCost.toFixed(2);
    this.unitCostFieldTarget.value = calculated.unitCost.toFixed(2);
    this.unitCostWithWastageFieldTarget.value =
      calculated.unitCostWithWastage.toFixed(2);
    this.grossMarginFieldTarget.value = calculated.grossMargin.toFixed(2);
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  }

  onIngredientsUpdate() {
    this.recalculate();
  }

  // Update individual sub-recipe displays when dropdown or quantity changes
  updateSubRecipeRow(event) {
    const row = event.target.closest("tr");
    const recipeSelect = row.querySelector('select[name*="[recipe_id]"]');
    const quantityInput = row.querySelector('input[name*="[quantity]"]');
    const unitPriceDisplay = row.querySelector(
      '[data-cost-calculator-target="subRecipeUnitPrice"]'
    );
    const totalCostDisplay = row.querySelector(
      '[data-cost-calculator-target="subRecipeTotalCost"]'
    );

    if (
      !recipeSelect ||
      !quantityInput ||
      !unitPriceDisplay ||
      !totalCostDisplay
    ) {
      return;
    }

    const recipeId = parseInt(recipeSelect.value);
    const quantity = this.parseValue(quantityInput.value);

    if (recipeId && recipeSelect.selectedOptions.length > 0) {
      const selectedOption = recipeSelect.selectedOptions[0];
      const unitCost = this.parseValue(selectedOption.dataset.unitCost || 0);

      // Update unit price display
      unitPriceDisplay.textContent = this.formatCurrency(unitCost);

      // Update total cost display
      const totalCost = quantity > 0 ? quantity * unitCost : 0;
      totalCostDisplay.textContent = this.formatCurrency(totalCost);
    } else {
      // Clear displays if no recipe selected
      unitPriceDisplay.textContent = "-";
      totalCostDisplay.textContent = "-";
    }

    // Trigger overall recalculation
    this.recalculate();
  }

  // Update visual profitability indicators based on margin rate
  updateProfitabilityDisplay(calculated) {
    const marginRate = calculated.marginRate;
    const grossMargin = calculated.grossMargin;
    const netPrice = calculated.netPrice;
    const hasIngredients = calculated.totalIngredientCost > 0;

    // Show/hide profitability system based on whether there are ingredients
    if (!hasIngredients) {
      this.hideProfitabilitySystem();
      return;
    }

    const profitability = this.getProfitabilityLevel(marginRate);

    // Show profitability system and update it
    this.showProfitabilitySystem();

    // Update profitability banner
    this.updateProfitabilityBanner(profitability, marginRate);

    // Update stat boxes with color coding
    this.updateStatBoxes(profitability, grossMargin);

    // Update recommendations
    this.updateRecommendations(
      profitability,
      marginRate,
      netPrice,
      calculated.unitCostWithWastage
    );
  }

  getProfitabilityLevel(marginRate) {
    if (marginRate >= 70) {
      return {
        level: "excellent",
        label: "Excellente",
        description: "🚀 Recette très rentable ! Marge exceptionnelle",
        color: "success",
        bgColor: "bg-success/10",
        borderColor: "border-success/30",
        textColor: "text-success",
      };
    } else if (marginRate >= 50) {
      return {
        level: "very-good",
        label: "Très bonne",
        description: "✨ Très bonne rentabilité, continuez ainsi !",
        color: "success",
        bgColor: "bg-success/10",
        borderColor: "border-success/30",
        textColor: "text-success",
      };
    } else if (marginRate >= 30) {
      return {
        level: "good",
        label: "Bonne",
        description: "👍 Rentabilité correcte, peut être optimisée",
        color: "warning",
        bgColor: "bg-warning/10",
        borderColor: "border-warning/30",
        textColor: "text-warning",
      };
    } else if (marginRate >= 10) {
      return {
        level: "fair",
        label: "Correcte",
        description: "⚠️ Marge faible, surveillez vos coûts",
        color: "warning",
        bgColor: "bg-warning/10",
        borderColor: "border-warning/30",
        textColor: "text-warning",
      };
    } else if (marginRate >= 0) {
      return {
        level: "poor",
        label: "Faible",
        description: "⚡ Marge très faible, action requise",
        color: "error",
        bgColor: "bg-error/10",
        borderColor: "border-error/30",
        textColor: "text-error",
      };
    } else {
      return {
        level: "deficit",
        label: "Déficitaire",
        description: "🔴 Recette déficitaire ! Révision urgente",
        color: "error",
        bgColor: "bg-error/10",
        borderColor: "border-error/30",
        textColor: "text-error",
      };
    }
  }

  updateProfitabilityBanner(profitability, marginRate) {
    // Update banner background and border
    this.profitabilityBannerTarget.className = `mb-4 p-3 rounded-lg border-l-4 transition-all duration-500 ${profitability.bgColor} ${profitability.borderColor}`;

    // Update indicator dot
    this.profitabilityIndicatorTarget.className = `w-3 h-3 rounded-full animate-pulse bg-${profitability.color}`;

    // Update title and description
    this.profitabilityTitleTarget.textContent = `Rentabilité : ${
      profitability.label
    } (${marginRate.toFixed(1)}%)`;
    this.profitabilityTitleTarget.className = `font-semibold text-sm ${profitability.textColor}`;
    this.profitabilityDescriptionTarget.textContent = profitability.description;

    // Update badge
    this.profitabilityBadgeTarget.textContent = profitability.label;
    this.profitabilityBadgeTarget.className = `badge badge-${profitability.color}`;

    // Keep the wrapper transparent — colour is conveyed by the banner and the
    // margin stat cards, not by a full-width background wash.
    this.profitabilityRowTarget.className = "transition-all duration-500";
  }

  updateStatBoxes(profitability, grossMargin) {
    const statBase =
      "flex flex-col items-center justify-center gap-1 text-center rounded-xl p-4 border transition-all duration-300";

    // Update gross margin stat with color coding
    if (grossMargin > 0) {
      this.grossMarginStatTarget.className = `${statBase} border-success/30 ${profitability.bgColor}`;
    } else {
      this.grossMarginStatTarget.className = `${statBase} border-error/30 bg-error/5`;
    }

    // Update margin rate stat with color coding
    this.marginRateStatTarget.className = `${statBase} border-base-300 ${profitability.bgColor}`;
    this.marginRateIndicatorTarget.className = `w-2 h-2 rounded-full bg-${profitability.color}`;
  }

  updateRecommendations(profitability, marginRate, netPrice, unitCost) {
    let recommendation = "";

    if (profitability.level === "excellent") {
      recommendation =
        "Félicitations ! Votre recette est très rentable. Vous pourriez même légèrement réduire le prix pour être plus compétitif.";
    } else if (profitability.level === "very-good") {
      recommendation =
        "Très bien ! Maintenez cette rentabilité en surveillant vos coûts d'approvisionnement.";
    } else if (profitability.level === "good") {
      recommendation =
        "Bonne marge. Essayez d'optimiser vos achats ou d'ajuster légèrement votre prix de vente.";
    } else if (profitability.level === "fair") {
      recommendation = `Marge faible. Considérez augmenter votre prix ou réduire vos coûts. Coût actuel: ${this.formatCurrency(
        unitCost
      )}`;
    } else if (profitability.level === "poor") {
      recommendation =
        "Marge critique ! Révisez vos fournisseurs, portions ou augmentez significativement votre prix.";
    } else {
      recommendation =
        "🚨 Attention ! Cette recette vous fait perdre de l'argent. Action immédiate requise sur le prix ou les coûts.";
    }

    this.recommendationTextTarget.textContent = recommendation;
    this.recommendationsPanelTarget.style.display = "block";
  }

  hideProfitabilitySystem() {
    // Reset to neutral state when no ingredients
    this.profitabilityBannerTarget.className =
      "mb-4 p-3 rounded-lg border-l-4 transition-all duration-500 bg-base-100 border-base-300";
    this.profitabilityIndicatorTarget.className =
      "w-3 h-3 rounded-full bg-base-300";
    this.profitabilityTitleTarget.textContent =
      "Ajoutez des ingrédients pour calculer la rentabilité";
    this.profitabilityTitleTarget.className =
      "font-semibold text-sm text-base-content/60";
    this.profitabilityDescriptionTarget.textContent =
      "Commencez par ajouter des ingrédients ou sous-recettes";
    this.profitabilityBadgeTarget.textContent = "-";
    this.profitabilityBadgeTarget.className = "badge badge-ghost";
    this.profitabilityRowTarget.className = "transition-all duration-500";

    // Reset stat boxes
    const statBase =
      "flex flex-col items-center justify-center gap-1 text-center rounded-xl p-4 border border-base-300 bg-base-200/40 transition-all duration-300";
    this.grossMarginStatTarget.className = statBase;
    this.marginRateStatTarget.className = statBase;
    this.marginRateIndicatorTarget.className =
      "w-2 h-2 rounded-full bg-base-300";

    // Hide recommendations
    this.recommendationsPanelTarget.style.display = "none";
  }

  showProfitabilitySystem() {
    // Just ensure the system is visible - actual styling will be done by other methods
    this.recommendationsPanelTarget.style.display = "block";
  }

  parseValue(value) {
    if (typeof value !== "string") return value || 0;

    // Handle French currency formatting: "1 234,56 €" or "1,234.56"
    let cleanValue = value
      .replace(/[€\s]/g, "") // Remove currency symbol and spaces
      .replace(/[^\d,.-]/g, "") // Keep only digits, commas, dots, and minus
      .replace(/\s/g, ""); // Remove any remaining spaces

    // Handle French decimal separator (comma)
    if (cleanValue.includes(",")) {
      // If there's both comma and dot, assume comma is decimal separator
      if (cleanValue.includes(".")) {
        cleanValue = cleanValue.replace(/\./g, "").replace(",", ".");
      } else {
        cleanValue = cleanValue.replace(",", ".");
      }
    }

    const result = parseFloat(cleanValue) || 0;
    return isNaN(result) ? 0 : result;
  }
}
